<template>
  <dialog
    class="st-add-offer-panel-dialog"
    aria-labelledby="add-offer-panel-title"
    @cancel.prevent="emit(CLOSE)"
    @close="resetForm"
  >
    <div class="st-add-offer-panel">
      <div class="st-add-offer-panel__header">
        <StHeadline id="add-offer-panel-title" :level="2">Lägg till</StHeadline>
        <StButton rectangular quaternary type="button" @click="emit(CLOSE)">
          <StIcon name="st-close-big" />
          Stäng
        </StButton>
      </div>

      <form class="st-add-offer-panel__form" @submit.prevent="addOffer">
        <StFormGroup label="Erbjudandenamn" required>
          <StSelect
            v-model="selectedOfferId"
            :items="offerOptions"
            empty-option-text="Välj specialerbjudande"
            required
          />
        </StFormGroup>

        <StFormGroup
          for-id="offer-organization-number"
          label="Organisationsnummer"
          required
          :validation="v$.organizationNumber"
          :validation-errors="AddOfferValidationsErrors.organizationNumber"
        >
          <StInput
            id="offer-organization-number"
            v-model="organizationNumber"
            required
            :invalid="v$.organizationNumber.$error"
          />
        </StFormGroup>

        <StFormGroup label="Tillåt abonnemang" required>
          <StSelect
            v-model="subscription"
            :items="subscriptionOptions"
            :has-empty="false"
            required
          />
        </StFormGroup>

        <StError v-if="errorMessage" :error-message="errorMessage" />

        <div class="st-add-offer-panel__buttons">
          <StButton
            rectangular
            secondary
            full-width
            type="button"
            @click="emit(CLOSE)"
          >
            Avbryt
          </StButton>
          <StButton
            rectangular
            full-width
            type="submit"
            :is-loading="isLoading"
          >
            Lägg till
          </StButton>
        </div>
      </form>
    </div>
  </dialog>
</template>

<script setup>
import {
  StButton,
  StError,
  StFormGroup,
  StHeadline,
  StIcon,
  StInput,
  StSelect,
} from "skanetrafiken";
import { CLOSE } from "@/constants";
import { useVuelidate } from "@vuelidate/core";
import AddOfferValidations from "@/validators/add-offer-validations";
import AddOfferValidationsErrors from "@/validators/add-offer-validations-errors";
import { computed, ref } from "vue";

const emit = defineEmits(["add", "close"]);
const props = defineProps({
  offers: {
    type: Array,
    default: () => [],
  },
  isLoading: Boolean,
  errorMessage: String,
});

const subscriptionOptions = [
  { id: "true", name: "Ja" },
  { id: "false", name: "Nej" },
];

const selectedOfferId = ref("");
const organizationNumber = ref("");
const subscription = ref("true");
const v$ = useVuelidate(AddOfferValidations, { organizationNumber });

const offerOptions = computed(() =>
  Array.from(
    new Map(
      props.offers.map((offer) => [
        offer.id,
        { id: offer.id, name: offer.name },
      ]),
    ).values(),
  ),
);

const canSubmit = computed(
  () =>
    selectedOfferId.value !== "" &&
    organizationNumber.value.trim().length > 0 &&
    !v$.value.organizationNumber.$invalid &&
    !props.isLoading,
);

const addOffer = () => {
  v$.value.organizationNumber.$touch();
  if (!canSubmit.value) return;

  emit("add", {
    offerId: Number(selectedOfferId.value),
    subscription: subscription.value === "true",
    organizationNumber: organizationNumber.value.trim().replace(/-/g, ""),
  });
};

const resetForm = () => {
  selectedOfferId.value = "";
  organizationNumber.value = "";
  subscription.value = "true";
  v$.value.$reset();
};
</script>

<style scoped lang="scss">
@import "~skanetrafiken/dist/scss/main.scss";

.st-add-offer-panel-dialog {
  position: fixed;
  inset: 0;
  width: 100vw;
  max-width: none;
  height: 100vh;
  max-height: none;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;

  &::backdrop {
    background: transparent;
  }
}

.st-add-offer-panel {
  position: absolute;
  top: 0;
  right: 0;
  width: min(rem-calc(400), 100vw);
  height: 100vh;
  padding: rem-calc(32);
  overflow-y: auto;
  background-color: var(--background-color-secondary);
  box-shadow: rem-calc(-8) 0 rem-calc(24) rgba(0, 0, 0, 0.14);

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: rem-calc(16);
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: rem-calc(20);
    padding-top: rem-calc(24);
  }

  &__buttons {
    display: flex;
    gap: rem-calc(16);
    margin-top: rem-calc(4);
  }

  @media (max-width: rem-calc(600)) {
    width: 100%;
  }
}
</style>
